"use client";

import { apiGetUserInfo } from "@/api/common";
import { setSentryUser } from "@/lib/sentry/instrument";
import { getToken } from "@/store/auth";
import { useCallback } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

// 刷新原因枚举
export enum RefreshReason {
    ORDER_COMPLETED = "order:completed",
    ORDER_CANCELED = "order:canceled",
    PAGE_ENTER_ME = "page:enter:me",
    PAGE_ENTER_PROFILE = "page:enter:profile",
    DEPOSIT_SUCCESS = "deposit:success",
    WITHDRAW_SUCCESS = "withdraw:success",
    TRANSFER_SUCCESS = "transfer:success",
    MANUAL = "manual",
}

const canRequestUserInfo = () => Boolean( getToken() );

const getUserInfoDebugContext = () => ( {
    hasToken: canRequestUserInfo(),
    pathname: typeof window === "undefined" ? "" : window.location.pathname,
    timestamp: Date.now(),
} );

interface UserInfoState {
    // 基础信息
    merchantNo: string;
    userId: number;
    userName: string;
    userDesc: string;
    userAvatar: string;
    userInviteCode: string;
    userPhone: string;

    // 余额相关
    userBalance: string;
    totalBalance: string;
    canWithdrawAmount: string;
    shareBalance: string;
    portfolio: number;

    // 充值/提现
    recharge: string;
    withdraw: string;
    rechargeAmount: string;

    // 个人中心统计数据
    potentialPayout: string;
    profit: string;
    volume: string;
    predictions: string;
    tradeDataVisibility: {
        potentialPayout: boolean;
        profit: boolean;
        volume: boolean;
        predictions: boolean;
        positions: boolean;
        trades: boolean;
    } | null;

    // 提现配置
    withdrawFeeRate: string;
    singleWithdrawalLimitAmount: string;
    dailyWithdrawalLimitCount: number;

    // 代理相关
    isAgent: number;
    agentLevel: number;

    // 其他
    packageId: number | null;
    channelId: string;
    createTime: number | null;
    updateTime: number | null;
    lastLoginTime: number | null;

    // 状态管理
    isLoaded: boolean;
    isFetching: boolean;
    lastRefreshTime: number; // 上次刷新时间戳
    refreshDebounceMs: number; // 防抖间隔（默认500ms）

    // 方法
    fetchUserInfo: ( source?: string ) => Promise<void>;
    refreshUserInfo: ( source?: string ) => Promise<void>; // 强制刷新，忽略缓存
    smartRefresh: ( reason: RefreshReason ) => Promise<void>; // 智能刷新（带防抖）
    forceRefresh: ( reason: RefreshReason ) => Promise<void>; // 强制刷新（忽略防抖）
    resetUserInfo: () => void;
}

export const useUserInfo = create<UserInfoState>()( devtools( ( set, get ) => ( {
    // 基础信息
    merchantNo: "",
    userId: 0,
    userName: "",
    userDesc: "",
    userAvatar: "",
    userInviteCode: "",
    userPhone: "",

    // 余额相关
    userBalance: "0",
    totalBalance: "0",
    canWithdrawAmount: "0",
    shareBalance: "0",
    portfolio: 0,

    // 充值/提现
    recharge: "0",
    withdraw: "0",
    rechargeAmount: "0",

    // 个人中心统计数据
    potentialPayout: "0",
    profit: "0",
    volume: "0",
    predictions: "0",
    tradeDataVisibility: null,

    // 提现配置
    withdrawFeeRate: "0",
    singleWithdrawalLimitAmount: "0",
    dailyWithdrawalLimitCount: 0,

    // 代理相关
    isAgent: 0,
    agentLevel: 0,

    // 其他
    packageId: null,
    channelId: "",
    createTime: null,
    updateTime: null,
    lastLoginTime: null,

    // 状态管理
    isLoaded: false,
    isFetching: false,
    lastRefreshTime: 0,
    refreshDebounceMs: 500,

    // 按需获取用户信息（有缓存）
    fetchUserInfo: async ( source = "unknown" ) => {
        const context = getUserInfoDebugContext();
        if ( !context.hasToken ) {
            return;
        }

        const { isFetching } = get();
        if ( isFetching ) {
            return;
        } // 只保留请求中判断，删除 isLoaded 检查

        set( { isFetching: true } );

        const [ is_ok, res ] = await apiGetUserInfo();

        if ( is_ok && res?.data ) {
            set( {
                // 基础信息
                merchantNo: res.data.merchantNo || "",
                userId: res.data.userId || 0,
                userName: res.data.userName || "",
                userDesc: res.data.userDesc || "",
                userAvatar: res.data.userAvatar || "",
                userInviteCode: res.data.userInviteCode || "",
                userPhone: res.data.userPhone || "",

                // 余额相关
                userBalance: res.data.userBalance || "0",
                totalBalance: res.data.totalBalance || "0",
                canWithdrawAmount: res.data.canWithdrawAmount || "0",
                shareBalance: res.data.shareBalance || "0",
                portfolio: res.data.portfolio || 0,

                // 充值/提现
                recharge: res.data.recharge || "0",
                withdraw: res.data.withdraw || "0",
                rechargeAmount: res.data.rechargeAmount || "0",

                // 个人中心统计数据
                potentialPayout: res.data.potentialPayout || "0",
                profit: res.data.profit || "0",
                volume: res.data.volume || "0",
                predictions: res.data.predictions || "0",
                tradeDataVisibility: ( () => {
                    try {
                        const visibilityStr = res.data.tradeDataVisibility;
                        if ( !visibilityStr ) {
                            return {
                                potentialPayout: true,
                                profit: true,
                                volume: true,
                                predictions: true,
                                positions: true,
                                trades: true,
                            };
                        }
                        const parsed = typeof visibilityStr === "string" ? JSON.parse( visibilityStr ) : visibilityStr;
                        return {
                            potentialPayout: parsed.potentialPayout ?? true,
                            profit: parsed.profit ?? true,
                            volume: parsed.volume ?? true,
                            predictions: parsed.predictions ?? true,
                            positions: parsed.positions ?? true,
                            trades: parsed.trades ?? true,
                        };
                    } catch {
                        return {
                            potentialPayout: true,
                            profit: true,
                            volume: true,
                            predictions: true,
                            positions: true,
                            trades: true,
                        };
                    }
                } )(),

                // 提现配置
                withdrawFeeRate: res.data.withdrawFeeRate || "0",
                singleWithdrawalLimitAmount: res.data.singleWithdrawalLimitAmount || "0",
                dailyWithdrawalLimitCount: res.data.dailyWithdrawalLimitCount || 0,

                // 代理相关
                isAgent: res.data.isAgent || 0,
                agentLevel: res.data.agentLevel || 0,

                // 其他
                packageId: res.data.packageId || null,
                channelId: res.data.channelId || "",
                createTime: res.data.createTime || null,
                updateTime: res.data.updateTime || null,
                lastLoginTime: res.data.lastLoginTime || null,

                // 状态管理
                isLoaded: true,
                isFetching: false,
            } );
            // 同步用户上下文到 Sentry，便于错误溯源
            setSentryUser( { id: res.data.userId || 0, username: res.data.userName || "" } );
        } else {
            set( { isFetching: false } );
        }
    },

    // 强制刷新用户信息（无缓存，用于交易后更新余额）
    refreshUserInfo: async ( source = "unknown" ) => {
        const context = getUserInfoDebugContext();
        if ( !context.hasToken ) {
            return;
        }

        const { isFetching } = get();
        if ( isFetching ) {
            return;
        }

        set( { isFetching: true } );

        const [ is_ok, res ] = await apiGetUserInfo();

        if ( is_ok && res?.data ) {
            set( {
                // 基础信息
                merchantNo: res.data.merchantNo || "",
                userId: res.data.userId || 0,
                userName: res.data.userName || "",
                userDesc: res.data.userDesc || "",
                userAvatar: res.data.userAvatar || "",
                userInviteCode: res.data.userInviteCode || "",
                userPhone: res.data.userPhone || "",

                // 余额相关
                userBalance: res.data.userBalance || "0",
                totalBalance: res.data.totalBalance || "0",
                canWithdrawAmount: res.data.canWithdrawAmount || "0",
                shareBalance: res.data.shareBalance || "0",
                portfolio: res.data.portfolio || 0,

                // 充值/提现
                recharge: res.data.recharge || "0",
                withdraw: res.data.withdraw || "0",
                rechargeAmount: res.data.rechargeAmount || "0",

                // 个人中心统计数据
                potentialPayout: res.data.potentialPayout || "0",
                profit: res.data.profit || "0",
                volume: res.data.volume || "0",
                predictions: res.data.predictions || "0",
                tradeDataVisibility: ( () => {
                    try {
                        const visibilityStr = res.data.tradeDataVisibility;
                        if ( !visibilityStr ) {
                            return {
                                potentialPayout: true,
                                profit: true,
                                volume: true,
                                predictions: true,
                                positions: true,
                                trades: true,
                            };
                        }
                        const parsed = typeof visibilityStr === "string" ? JSON.parse( visibilityStr ) : visibilityStr;
                        return {
                            potentialPayout: parsed.potentialPayout ?? true,
                            profit: parsed.profit ?? true,
                            volume: parsed.volume ?? true,
                            predictions: parsed.predictions ?? true,
                            positions: parsed.positions ?? true,
                            trades: parsed.trades ?? true,
                        };
                    } catch {
                        return {
                            potentialPayout: true,
                            profit: true,
                            volume: true,
                            predictions: true,
                            positions: true,
                            trades: true,
                        };
                    }
                } )(),

                // 提现配置
                withdrawFeeRate: res.data.withdrawFeeRate || "0",
                singleWithdrawalLimitAmount: res.data.singleWithdrawalLimitAmount || "0",
                dailyWithdrawalLimitCount: res.data.dailyWithdrawalLimitCount || 0,

                // 代理相关
                isAgent: res.data.isAgent || 0,
                agentLevel: res.data.agentLevel || 0,

                // 其他
                packageId: res.data.packageId || null,
                channelId: res.data.channelId || "",
                createTime: res.data.createTime || null,
                updateTime: res.data.updateTime || null,
                lastLoginTime: res.data.lastLoginTime || null,

                // 状态管理
                isLoaded: true,
                isFetching: false,
            } );
            // 同步用户上下文到 Sentry，便于错误溯源
            setSentryUser( { id: res.data.userId || 0, username: res.data.userName || "" } );
        } else {
            set( { isFetching: false } );
        }
    },

    // 智能刷新（带防抖）
    smartRefresh: async ( reason: RefreshReason ) => {
        if ( !canRequestUserInfo() ) {
            console.log( `[UserInfo] Skip refresh (guest): ${reason}` );
            return;
        }

        const now = Date.now();
        const { lastRefreshTime, refreshDebounceMs, isFetching } = get();

        // 正在请求中，跳过
        if ( isFetching ) {
            console.log( `[UserInfo] Skip refresh (fetching): ${reason}` );
            return;
        }

        // 防抖检查：500ms 内重复请求合并
        if ( now - lastRefreshTime < refreshDebounceMs ) {
            console.log( `[UserInfo] Skip refresh (debounce): ${reason}, last fresh ${now - lastRefreshTime}ms ago` );
            return;
        }

        console.log( `[UserInfo] Trigger refresh: ${reason}` );
        set( { lastRefreshTime: now } );
        await get().refreshUserInfo( `smartRefresh:${reason}` );
    },

    // 强制刷新（忽略防抖）
    forceRefresh: async ( reason: RefreshReason ) => {
        if ( !canRequestUserInfo() ) {
            console.log( `[UserInfo] Skip force refresh (guest): ${reason}` );
            return;
        }

        const { isFetching } = get();
        if ( isFetching ) {
            console.log( `[UserInfo] Skip refresh (fetching): ${reason}` );
            return;
        }

        console.log( `[UserInfo] Force refresh: ${reason}` );
        set( { lastRefreshTime: Date.now() } );
        await get().refreshUserInfo( `forceRefresh:${reason}` );
    },

    resetUserInfo: () => {
        // 登出时清除 Sentry 用户上下文，避免后续错误关联到已退出用户
        setSentryUser( null );
        set( {
            merchantNo: "",
            userId: 0,
            userName: "",
            userDesc: "",
            userAvatar: "",
            userInviteCode: "",
            userPhone: "",
            userBalance: "0",
            totalBalance: "0",
            canWithdrawAmount: "0",
            shareBalance: "0",
            portfolio: 0,
            recharge: "0",
            withdraw: "0",
            rechargeAmount: "0",
            potentialPayout: "0",
            profit: "0",
            volume: "0",
            predictions: "0",
            tradeDataVisibility: null,
            withdrawFeeRate: "0",
            singleWithdrawalLimitAmount: "0",
            dailyWithdrawalLimitCount: 0,
            isAgent: 0,
            agentLevel: 0,
            packageId: null,
            channelId: "",
            createTime: null,
            updateTime: null,
            lastLoginTime: null,
            isLoaded: false,
            isFetching: false,
        } );
    },
} ), { name: "UserInfo", enabled: process.env.NODE_ENV === "development" } ) );

// 导出统一刷新钩子
export function useRefreshUserBalance() {
    const smartRefresh = useUserInfo( ( state ) => state.smartRefresh );
    return useCallback( ( reason: RefreshReason ) => smartRefresh( reason ), [ smartRefresh ] );
}

export function useForceRefreshUserBalance() {
    const forceRefresh = useUserInfo( ( state ) => state.forceRefresh );
    return useCallback( ( reason: RefreshReason ) => forceRefresh( reason ), [ forceRefresh ] );
}
